import React, { useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { SPACE_QUESTION_NATIVE } from '@confluence/named-routes/entry-points/routes';

import { QuestionListQuery } from '../graphql/QuestionListQuery.graphql';
import type {
	QuestionListQuery as QuestionListQueryType,
	QuestionListQueryVariables as QuestionListQueryVariablesType,
} from '../graphql/__types__/QuestionListQuery';
import { useQueryWithError } from '../graphql/graphqlUtils';
import { ANSWER_CONTENT_TYPE } from '../contentType';
import { FilterType, getCQLForFilter } from '../QuestionsFilters';

import { mapQuestionDefaultValues } from './questionListUtils';
import { QUESTIONS_PER_PAGE } from './BaseQuestionsList';
import { BaseQuestionsListMacro } from './BaseQuestionsListMacro';

type SpaceQuestionsListMacroProps = {
	spaceKey: string;
	limit?: number;
	filter?: string;
	topics?: string[];
	includeTopics?: 'all' | 'any';
};

export const SpaceQuestionsListMacro: React.FC<SpaceQuestionsListMacroProps> = ({
	spaceKey,
	limit,
	filter,
	topics,
	includeTopics,
}) => {
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [cql, setCql] = useState<string>('');

	const filterType: FilterType =
		filter === 'popular'
			? FilterType.POPULAR
			: filter === 'unanswered'
				? FilterType.UNANSWERED
				: FilterType.RECENT;

	const questionsLimit = limit || QUESTIONS_PER_PAGE;

	const queryVariables = useMemo(
		() => ({
			cql,
			limit: questionsLimit,
			cloudId,
			answerContentType: ANSWER_CONTENT_TYPE,
		}),
		[cloudId, cql, questionsLimit],
	);

	const { data, loading, error } = useQueryWithError<
		QuestionListQueryType,
		QuestionListQueryVariablesType
	>(QuestionListQuery, {
		variables: queryVariables,
		skip: !cql,
	});

	const questions = useMemo(
		() =>
			data?.search?.nodes && data?.search?.nodes.length > 0
				? data?.search?.nodes.map((question) =>
						mapQuestionDefaultValues(question?.content, '', spaceKey),
					)
				: [],
		[data?.search?.nodes, spaceKey],
	);

	useEffect(() => {
		const generatedCql = getCQLForFilter(filterType, topics, includeTopics, spaceKey);
		setCql(generatedCql);
	}, [filterType, topics, includeTopics, spaceKey]);

	useEffect(() => {
		if (!loading && !error && data?.search) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'SpaceQuestionsListMacro',
				},
			}).fire();
		}
	}, [cql, loading, error, data?.search, createAnalyticsEvent]);

	return (
		<BaseQuestionsListMacro
			questions={questions}
			loading={loading || !cql}
			error={error}
			limit={limit || QUESTIONS_PER_PAGE}
			routerName={SPACE_QUESTION_NATIVE.name}
			defaultLimit={QUESTIONS_PER_PAGE}
		/>
	);
};
